<template>
    <div class="relative" ref="selectContainer">
        <!-- Selected value display / dropdown button -->
        <button
            type="button"
            @click="toggleDropdown"
            class="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 px-4 py-2 min-w-[250px] flex items-center justify-between"
            :class="{ 'ring-2 ring-indigo-500': isOpen }"
        >
            <span class="truncate">{{ selectedLabel }}</span>
            <svg
                class="w-4 h-4 ml-2 transition-transform"
                :class="{ 'rotate-180': isOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>

        <!-- Dropdown menu -->
        <Transition name="dropdown">
            <div
                v-if="isOpen"
                ref="dropdownMenu"
                class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                @click.stop
            >
                <div
                    ref="optionsContainer"
                    class="py-1"
                >
                    <div
                        v-for="(option, index) in localOptions"
                        :key="`option-${index}-${option.key || index}`"
                        :ref="el => { if (el) optionRefs[index] = el }"
                        :data-index="index"
                        draggable="true"
                        @dragstart="handleDragStart($event, index)"
                        @dragend="handleDragEnd"
                        @dragover="handleDragOver($event, index)"
                        @drop="handleDrop($event, index)"
                        @mousedown="handleMouseDown($event, index)"
                        @mouseup="handleMouseUp($event, index)"
                        @click="handleOptionClick(index)"
                        class="px-4 py-2 cursor-pointer select-none flex items-center justify-between"
                        :class="{
                            'bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200': selectedIndex === index,
                            'hover:bg-gray-100 dark:hover:bg-gray-700': selectedIndex !== index && !isDragging,
                            'opacity-50': isDragging && draggingIndex === index,
                            'bg-gray-200 dark:bg-gray-600': isDragging && dragOverIndex === index
                        }"
                    >
                        <span class="flex-1 truncate">{{ getOptionLabel(option) }}</span>
                        <div class="flex items-center gap-2 ml-2">
                            <svg
                                v-if="selectedIndex === index"
                                class="w-4 h-4 text-indigo-600 dark:text-indigo-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <svg
                                class="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing"
                                :class="{ 'cursor-grabbing': draggingIndex === index }"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M4 8h16M4 16h16"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: {
        type: Number,
        default: 0
    },
    options: {
        type: Array,
        required: true
    },
    optionLabel: {
        type: String,
        default: 'title'
    },
    placeholder: {
        type: String,
        default: 'Select an option'
    }
});

const emit = defineEmits(['update:modelValue', 'change', 'reorder']);

const isOpen = ref(false);
const selectedIndex = ref(props.modelValue);
const selectContainer = ref(null);
const dropdownMenu = ref(null);
const optionsContainer = ref(null);
const optionRefs = ref([]);

// Drag and drop state
const isDragging = ref(false);
const draggingIndex = ref(-1);
const dragOverIndex = ref(-1);
const localOptions = ref([...props.options]);

// Mouse interaction state
const mouseDownIndex = ref(-1);
const mouseDownTime = ref(0);
const hasMoved = ref(false);
const justCompletedDrag = ref(false);

const selectedLabel = computed(() => {
    if (selectedIndex.value >= 0 && selectedIndex.value < localOptions.value.length) {
        return getOptionLabel(localOptions.value[selectedIndex.value]);
    }
    return props.placeholder;
});

function getOptionLabel(option) {
    if (typeof option === 'string') {
        return option;
    }
    if (typeof option === 'object' && option !== null) {
        return option[props.optionLabel] || option.title || `Option ${selectedIndex.value + 1}`;
    }
    return '';
}

function toggleDropdown() {
    isOpen.value = !isOpen.value;
}

function closeDropdown() {
    isOpen.value = false;
}

// Mouse handlers for click detection
function handleMouseDown(event, index) {
    // Only handle if clicking on the option (not the drag handle)
    if (event.target.closest('svg') && event.target.closest('svg').parentElement?.querySelector('.cursor-grab')) {
        return; // Clicked on drag handle, let drag handle it
    }
    
    mouseDownIndex.value = index;
    mouseDownTime.value = Date.now();
    hasMoved.value = false;
}

function handleMouseUp(event, index) {
    // Don't select if we just completed a drag operation
    if (justCompletedDrag.value) {
        justCompletedDrag.value = false;
        mouseDownIndex.value = -1;
        hasMoved.value = false;
        return;
    }
    
    // Only select if:
    // 1. Same index as mouseDown
    // 2. Not dragging
    // 3. Mouse hasn't moved significantly
    if (
        mouseDownIndex.value === index &&
        !isDragging.value &&
        !hasMoved.value &&
        Date.now() - mouseDownTime.value < 300 // Click, not a long press
    ) {
        selectOption(index);
    }
    
    mouseDownIndex.value = -1;
    hasMoved.value = false;
}

function handleOptionClick(index) {
    // This handles clicks that weren't drag operations
    if (!isDragging.value && draggingIndex.value === -1) {
        selectOption(index);
    }
}

function selectOption(index) {
    selectedIndex.value = index;
    emit('update:modelValue', index);
    emit('change', index);
    closeDropdown();
}

// Drag and drop handlers
function handleDragStart(event, index) {
    isDragging.value = true;
    draggingIndex.value = index;
    hasMoved.value = true;
    
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', index.toString());
    
    // Add dragging class for visual feedback
    if (optionRefs.value[index]) {
        optionRefs.value[index].classList.add('dragging');
    }
    
    // Create a text-only ghost image (shadow effect)
    const optionText = getOptionLabel(localOptions.value[index]);
    const dragImage = document.createElement('div');
    dragImage.textContent = optionText;
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-9999px';
    dragImage.style.left = '-9999px';
    dragImage.style.padding = '8px 16px';
    dragImage.style.fontSize = '14px';
    dragImage.style.color = 'transparent';
    dragImage.style.textShadow = '0 0 8px rgba(0, 0, 0, 0.5), 0 0 12px rgba(0, 0, 0, 0.3)';
    dragImage.style.fontWeight = '500';
    dragImage.style.whiteSpace = 'nowrap';
    dragImage.style.background = 'transparent';
    dragImage.style.border = 'none';
    dragImage.style.pointerEvents = 'none';
    
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 10, 20);
    
    // Remove ghost image after a short delay
    setTimeout(() => {
        if (dragImage.parentNode) {
            dragImage.parentNode.removeChild(dragImage);
        }
    }, 0);
}

function handleDragEnd() {
    // Remove dragging class
    if (draggingIndex.value >= 0 && optionRefs.value[draggingIndex.value]) {
        optionRefs.value[draggingIndex.value].classList.remove('dragging');
    }
    
    // Mark that we just completed a drag to prevent selection on mouseup
    if (isDragging.value) {
        justCompletedDrag.value = true;
        // Reset the flag after a short delay to allow mouseup to check it
        setTimeout(() => {
            justCompletedDrag.value = false;
        }, 100);
    }
    
    isDragging.value = false;
    draggingIndex.value = -1;
    dragOverIndex.value = -1;
    hasMoved.value = false;
    
    // Reset mouse state
    mouseDownIndex.value = -1;
}

function handleDragOver(event, index) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    if (draggingIndex.value !== index) {
        dragOverIndex.value = index;
    }
}

function handleDrop(event, index) {
    event.preventDefault();
    event.stopPropagation();
    
    if (draggingIndex.value === -1 || draggingIndex.value === index) {
        dragOverIndex.value = -1;
        return;
    }
    
    const oldIndex = draggingIndex.value;
    const newIndex = index;
    
    // Mark that we just completed a drag to prevent selection on mouseup
    justCompletedDrag.value = true;
    
    // Reorder options
    const draggedOption = localOptions.value[oldIndex];
    localOptions.value.splice(oldIndex, 1);
    localOptions.value.splice(newIndex, 0, draggedOption);
    
    // Update selected index if needed
    if (selectedIndex.value === oldIndex) {
        selectedIndex.value = newIndex;
    } else if (oldIndex < selectedIndex.value && newIndex >= selectedIndex.value) {
        selectedIndex.value--;
    } else if (oldIndex > selectedIndex.value && newIndex <= selectedIndex.value) {
        selectedIndex.value++;
    }
    
    // Emit reorder event
    emit('reorder', {
        oldIndex,
        newIndex,
        options: [...localOptions.value]
    });
    
    dragOverIndex.value = -1;
    draggingIndex.value = -1;
    
    // Reset the flag after a short delay
    setTimeout(() => {
        justCompletedDrag.value = false;
    }, 100);
}

// Track mouse movement to detect drags
let mouseMoveHandler = null;

onMounted(() => {
    mouseMoveHandler = (event) => {
        if (mouseDownIndex.value >= 0) {
            // Check if mouse has moved significantly
            const deltaX = Math.abs(event.movementX || 0);
            const deltaY = Math.abs(event.movementY || 0);
            if (deltaX > 5 || deltaY > 5) {
                hasMoved.value = true;
            }
        }
    };
    
    window.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    if (mouseMoveHandler) {
        window.removeEventListener('mousemove', mouseMoveHandler);
    }
    document.removeEventListener('click', handleClickOutside);
});

function handleClickOutside(event) {
    if (selectContainer.value && !selectContainer.value.contains(event.target)) {
        closeDropdown();
    }
}

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
    if (newValue !== selectedIndex.value) {
        selectedIndex.value = newValue;
    }
});

// Watch for options changes
watch(() => props.options, (newOptions) => {
    // Only update if the options array reference or length changed
    // This prevents overwriting local reordering when parent updates options
    if (newOptions.length !== localOptions.value.length) {
        localOptions.value = [...newOptions];
    } else {
        // Check if options are actually different (content changed, not just order)
        const hasContentChanges = newOptions.some((opt, idx) => {
            const oldOpt = localOptions.value[idx];
            if (!oldOpt) return true;
            // Compare by id if available, otherwise by title/content
            if ((opt._id || opt.id) && (oldOpt._id || oldOpt.id)) {
                return (opt._id || opt.id) !== (oldOpt._id || oldOpt.id);
            }
            return JSON.stringify(opt) !== JSON.stringify(oldOpt);
        });
        
        // If content changed (not just order), update from parent
        // Otherwise, preserve local order
        if (hasContentChanges) {
            localOptions.value = [...newOptions];
        }
    }
}, { deep: true });
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
}

.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
}

.dragging {
    cursor: grabbing !important;
    background: transparent !important;
    border: none !important;
    color: transparent !important;
    text-shadow: 0 0 8px rgba(0, 0, 0, 0.5), 0 0 12px rgba(0, 0, 0, 0.3) !important;
}

.dragging svg {
    opacity: 0;
}

/* Hide scrollbar but keep functionality */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #475569;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #64748b;
}
</style>
